import { action, computed, makeObservable, observable } from "mobx";

import { ColumnConfigStore } from "./column-config-store";
import { ScorecardStore } from "./scorecard-store";
import { SheetDataStore } from "./sheet-data-store";

import type { SmartSheet } from "@/queries/smart-tables/types";

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export type EvalRunStatus = "running" | "completed" | "aborted";

export interface TabConfig {
  /** Stable unique identifier (server-assigned sheet UUID). */
  id: string;
  /** Display label shown in the tab navigator. */
  label: string;
  /** The server-assigned sheet ID (same as `id` after API hydration). */
  sheetId: string;
  /** True while this tab is pending server creation (optimistic insert). */
  isPending?: boolean;
}

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

const generateTempId = (): string => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_LABEL = (n: number) => `Sheet ${n}`;
const MAX_KEPT_MOUNTED_SHEETS = 3;

const evalRunningStorageKey = (sheetId: string): string => `smart-table-eval-running:${sheetId}`;
const evalExpectedRowCountStorageKey = (sheetId: string): string =>
  `smart-table-eval-expected-rows:${sheetId}`;
const evalAbortedStorageKey = (sheetId: string): string => `smart-table-eval-aborted:${sheetId}`;
const evalRunStatusStorageKey = (sheetId: string): string =>
  `smart-table-eval-run-status:${sheetId}`;

const VALID_EVAL_RUN_STATUSES = new Set<EvalRunStatus>(["running", "completed", "aborted"]);

const readEvalRunStatusFromSession = (sheetId: string): EvalRunStatus | null => {
  try {
    const raw = sessionStorage.getItem(evalRunStatusStorageKey(sheetId));
    if (raw === "running" || raw === "completed" || raw === "aborted") {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
};

const readExpectedRowCountFromSession = (sheetId: string): number | null => {
  try {
    const raw = sessionStorage.getItem(evalExpectedRowCountStorageKey(sheetId));
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  } catch {
    return null;
  }
};

const readEvalAbortedFromSession = (sheetId: string): boolean => {
  try {
    return sessionStorage.getItem(evalAbortedStorageKey(sheetId)) === "1";
  } catch {
    return false;
  }
};

export interface VersionColumnConfigTarget {
  sheetId: string;
  versionId: string;
  columnId: string;
  columnTitle: string;
  parentColumnId?: string;
  compositionSource?: string;
}

export type SmartTableRightPanel =
  | "column-config"
  | "version-history"
  | "version-column-config"
  | "score"
  | null;

// ────────────────────────────────────────────
// SmartTableStore
//
// Manages the ordered list of sheet tabs and tracks which one is active.
//
// API-backed version:
// - Tabs are hydrated from SmartSheet[] API response
// - addTabOptimistic / removeTab / renameTab trigger API mutations (handled by the page)
// - SheetDataStore is lazily created per tab and destroyed on removal
// ────────────────────────────────────────────

export class SmartTableStore {
  /** The smart table UUID from route params. */
  smartTableId = "";

  /** Display title from API. */
  title = "";

  /** Ordered list of tabs displayed in the navigator. */
  tabs: TabConfig[] = [];

  /** ID of the currently active (visible) tab. */
  activeTabId = "";
  /**
   * When set, the next `hydrateFromSheets` prefers this sheet over preserving
   * the previous active tab (used when Eval SDK creates an experiment sheet).
   */
  pendingActivateSheetId: string | null = null;

  /**
   * Sheets with an in-flight Eval SDK run. Keeps the above-grid running banner
   * visible during case execution (before scorecard/compute starts).
   */
  evalRunningSheetIds: string[] = [];

  /**
   * Planned case counts from Eval SDK (`expected_row_count`) so the banner can
   * show "3 of 10" while rows are still being written.
   */
  evalExpectedRowCounts: Record<string, number> = {};

  /**
   * Explicit Eval SDK populate lifecycle (`eval_run_status`). Source of truth
   * for the Running banner during case execution.
   */
  evalRunStatuses: Record<string, EvalRunStatus> = {};

  /**
   * Sheets whose eval populate was abandoned (Ctrl+C / stall). Prevents Redis
   * `expected_row_count` rehydrate from putting the banner back into Running.
   */
  evalAbortedSheetIds: string[] = [];

  /** Active sheet followed by the most recently active sheets retained in the DOM. */
  _keptMountedSheetIds: string[] = [];

  /** ID of the sheet currently showing version history. */
  versionHistorySheetId: string | null = null;

  /** ID of the specific version being previewed. */
  previewVersionId: string | null = null;

  /** ID of the version currently loading for preview. */
  previewLoadingVersionId: string | null = null;

  /**
   * True while the version-history panel is animating closed.
   * Used so UI can keep preview content mounted until close completes.
   */
  versionHistoryClosing = false;

  /**
   * Canonical right-panel slot for Table editor surfaces.
   *
   * All right-side panels (column config, version history, score) map into
   * this single state so the page can enforce "one panel at a time".
   */
  rightPanel: SmartTableRightPanel = null;

  /** Selected readonly column target while previewing version history. */
  versionColumnConfigTarget: VersionColumnConfigTarget | null = null;

  /**
   * Per-tab ColumnConfigStore — keyed by tab id.
   * Lifetime is tied to the tab. Automatically cleaned up on tab removal.
   */
  private readonly _columnConfigs = new Map<string, ColumnConfigStore>();

  /**
   * Per-tab SheetDataStore — keyed by tab id.
   * Owns grid data (columns, rows, cells) for each sheet.
   */
  private readonly _sheetDataStores = new Map<string, SheetDataStore>();

  /**
   * Per-tab ScorecardStore — keyed by tab id.
   * Owns scorecard sidebar navigation and optimistic config state.
   */
  private readonly _scorecardStores = new Map<string, ScorecardStore>();

  constructor() {
    makeObservable(this, {
      smartTableId: observable,
      title: observable,
      tabs: observable,
      activeTabId: observable,
      pendingActivateSheetId: observable,
      evalRunningSheetIds: observable,
      evalExpectedRowCounts: observable,
      evalRunStatuses: observable,
      evalAbortedSheetIds: observable,
      _keptMountedSheetIds: observable,
      versionHistorySheetId: observable,
      previewVersionId: observable,
      previewLoadingVersionId: observable,
      versionHistoryClosing: observable,
      rightPanel: observable,
      versionColumnConfigTarget: observable,
      // Actions
      hydrateFromSheets: action,
      addTabOptimistic: action,
      reconcileTab: action,
      removeTab: action,
      renameTab: action,
      setActiveTab: action,
      activateExternalSheet: action,
      markEvalRunning: action,
      clearEvalRunning: action,
      markEvalAborted: action,
      setEvalRunStatus: action,
      setEvalExpectedRowCount: action,
      clearEvalExpectedRowCount: action,
      restoreEvalRunningFromSession: action,
      moveTab: action,
      _recordMountedSheetAccess: action,
      setTitle: action,
      openVersionHistory: action,
      closeVersionHistory: action,
      beginVersionHistoryClose: action,
      completeVersionHistoryClose: action,
      resetVersionPreviewState: action,
      setPreviewVersionId: action,
      startVersionPreviewLoad: action,
      finishVersionPreviewLoad: action,
      openVersionColumnConfig: action,
      backToVersionHistoryFromColumnConfig: action,
      clearVersionColumnConfigTarget: action,
      setRightPanel: action,
      closeAllColumnConfigSidebars: action,
      // Computed
      activeIndex: computed,
      activeTab: computed,
      canRemove: computed,
      isVersionHistoryOpen: computed,
      shouldHoldVersionPreviewState: computed,
      keptMountedSheetIds: computed,
    });
  }

  _recordMountedSheetAccess = (id: string): void => {
    this._keptMountedSheetIds = [
      id,
      ...this._keptMountedSheetIds.filter(existingId => existingId !== id),
    ].slice(0, MAX_KEPT_MOUNTED_SHEETS);
  };

  // ── Bootstrap & Hydration ─────────────────

  setTitle = (title: string): void => {
    this.title = title;
  };

  /**
   * Hydrate tabs from API-fetched sheets.
   * Preserves the active tab if it still exists.
   */
  hydrateFromSheets = (
    sheets: SmartSheet[],
    tableId: string,
    initialTabId?: string | null,
  ): void => {
    const isTableChange = Boolean(this.smartTableId && this.smartTableId !== tableId);

    if (isTableChange) {
      this.activeTabId = "";
      this.pendingActivateSheetId = null;
      this.evalRunningSheetIds = [];
      this.evalExpectedRowCounts = {};
      this.evalRunStatuses = {};
      this.evalAbortedSheetIds = [];
      this.versionHistoryClosing = false;
      this.versionHistorySheetId = null;
      this.resetVersionPreviewState();
      this.rightPanel = null;
      this._columnConfigs.clear();
      this._sheetDataStores.clear();
      this._scorecardStores.clear();
      this._keptMountedSheetIds = [];
    }

    this.smartTableId = tableId;

    const prevActiveId = this.activeTabId;

    const sortedSheets = [...sheets].sort((a, b) => a.index - b.index);

    this.tabs = sortedSheets.map(sheet => ({
      id: sheet.id,
      sheetId: sheet.id,
      label: sheet.title,
    }));

    // Prefer an externally requested activation (Eval SDK sheet create) over
    // preserving the previous tab — otherwise hydrate keeps the empty sheet
    // the user was staring at while the experiment sheet fills in the background.
    if (this.pendingActivateSheetId && this.tabs.some(t => t.id === this.pendingActivateSheetId)) {
      this.activeTabId = this.pendingActivateSheetId;
      this.pendingActivateSheetId = null;
    } else if (!isTableChange && this.tabs.some(t => t.id === prevActiveId)) {
      this.activeTabId = prevActiveId;
    } else if (initialTabId && this.tabs.some(t => t.id === initialTabId)) {
      this.activeTabId = initialTabId;
    } else if (this.tabs.length > 0) {
      this.activeTabId = this.tabs[0].id;
    }

    if (this.activeTabId) {
      this._recordMountedSheetAccess(this.activeTabId);
    }

    this._keptMountedSheetIds = this._keptMountedSheetIds.filter(tabId =>
      this.tabs.some(t => t.id === tabId),
    );

    // Clean up stores for removed tabs
    for (const tabId of this._columnConfigs.keys()) {
      if (!this.tabs.some(t => t.id === tabId)) {
        this._columnConfigs.delete(tabId);
        this._sheetDataStores.delete(tabId);
      }
    }

    this.restoreEvalRunningFromSession(this.tabs.map(tab => tab.id));

    for (const sheet of sortedSheets) {
      if (
        sheet.eval_run_status === "running" ||
        sheet.eval_run_status === "completed" ||
        sheet.eval_run_status === "aborted"
      ) {
        this.setEvalRunStatus(sheet.id, sheet.eval_run_status);
      }
      if (typeof sheet.expected_row_count === "number" && sheet.expected_row_count >= 0) {
        this.setEvalExpectedRowCount(sheet.id, sheet.expected_row_count);
      }
    }
  };

  // ── Queries ───────────────────────────────

  get activeIndex(): number {
    return this.tabs.findIndex(t => t.id === this.activeTabId);
  }

  get activeTab(): TabConfig | null {
    return this.tabs[this.activeIndex] ?? null;
  }

  get canRemove(): boolean {
    return this.tabs.length > 1;
  }

  get isVersionHistoryOpen(): boolean {
    return this.versionHistorySheetId !== null;
  }

  get shouldHoldVersionPreviewState(): boolean {
    return this.versionHistoryClosing;
  }

  get keptMountedSheetIds(): string[] {
    return this._keptMountedSheetIds;
  }

  shouldKeepSheetMounted(sheetId: string): boolean {
    return this._keptMountedSheetIds.includes(sheetId);
  }

  /**
   * Return the ColumnConfigStore for `tabId`.
   * Lazily creates one if missing.
   */
  getColumnConfigStore(tabId: string): ColumnConfigStore {
    let store = this._columnConfigs.get(tabId);
    if (!store) {
      store = new ColumnConfigStore();
      this._columnConfigs.set(tabId, store);
    }
    return store;
  }

  /**
   * Return the SheetDataStore for `tabId`.
   * Lazily creates one if missing.
   */
  getSheetDataStore(tabId: string): SheetDataStore {
    let store = this._sheetDataStores.get(tabId);
    if (!store) {
      store = new SheetDataStore();
      this._sheetDataStores.set(tabId, store);
    }
    return store;
  }

  /**
   * Return an existing SheetDataStore for `tabId` without creating one.
   * Useful for read-only checks (e.g. tab status indicators) where we
   * should not allocate stores for sheets that have never been opened.
   */
  getExistingSheetDataStore(tabId: string): SheetDataStore | undefined {
    return this._sheetDataStores.get(tabId);
  }

  /**
   * Return the ScorecardStore for `tabId`.
   * Lazily creates one if missing.
   */
  getScorecardStore(tabId: string): ScorecardStore {
    let store = this._scorecardStores.get(tabId);
    if (!store) {
      store = new ScorecardStore();
      this._scorecardStores.set(tabId, store);
    }
    return store;
  }

  // ── Mutations ─────────────────────────────

  /**
   * Optimistically add a tab before the server responds.
   * Returns the temp id for reconciliation.
   */
  addTabOptimistic = (): string => {
    const tempId = generateTempId();
    const nextSheetNumber =
      this.tabs.reduce((max, tab) => {
        const match = tab.label.match(/^Sheet\s+(\d+)$/i);
        if (!match) {
          return max;
        }

        const parsed = Number.parseInt(match[1], 10);
        if (Number.isNaN(parsed)) {
          return max;
        }

        return Math.max(max, parsed);
      }, 0) + 1;
    const label = DEFAULT_LABEL(nextSheetNumber);
    this.tabs.push({ id: tempId, sheetId: tempId, label, isPending: true });
    this._columnConfigs.set(tempId, new ColumnConfigStore());
    this._sheetDataStores.set(tempId, new SheetDataStore());
    this.activeTabId = tempId;
    this._recordMountedSheetAccess(tempId);
    return tempId;
  };

  /**
   * Replace a temporary tab id with the server-assigned id.
   */
  reconcileTab = (tempId: string, serverSheet: SmartSheet): void => {
    const tab = this.tabs.find(t => t.id === tempId);
    if (tab) {
      tab.id = serverSheet.id;
      tab.sheetId = serverSheet.id;
      tab.label = serverSheet.title;
      tab.isPending = false;

      // Migrate stores to new id
      const colConfig = this._columnConfigs.get(tempId);
      if (colConfig) {
        this._columnConfigs.delete(tempId);
        this._columnConfigs.set(serverSheet.id, colConfig);
      }

      const sheetData = this._sheetDataStores.get(tempId);
      if (sheetData) {
        this._sheetDataStores.delete(tempId);
        this._sheetDataStores.set(serverSheet.id, sheetData);
      }

      this._keptMountedSheetIds = this._keptMountedSheetIds.map(tabId =>
        tabId === tempId ? serverSheet.id : tabId,
      );

      if (this.activeTabId === tempId) {
        this.activeTabId = serverSheet.id;
        this._recordMountedSheetAccess(serverSheet.id);
      }
    }
  };

  /**
   * Remove a tab and destroy its stores.
   * Focus shifts to nearest remaining tab.
   */
  removeTab = (id: string): void => {
    if (!this.canRemove) {
      return;
    }

    const index = this.tabs.findIndex(t => t.id === id);
    if (index === -1) {
      return;
    }

    const wasActive = this.activeTabId === id;
    this.tabs.splice(index, 1);
    this._keptMountedSheetIds = this._keptMountedSheetIds.filter(tabId => tabId !== id);
    this._columnConfigs.delete(id);
    this._sheetDataStores.delete(id);
    this._scorecardStores.delete(id);

    if (this.versionHistorySheetId === id) {
      this.closeVersionHistory();
    }

    if (wasActive) {
      const nextIndex = Math.max(0, index - 1);
      this.activeTabId = this.tabs[nextIndex]?.id ?? "";
      if (this.activeTabId) {
        this._recordMountedSheetAccess(this.activeTabId);
      }
    }
  };

  /**
   * Update the display label for a tab.
   * Trims whitespace; ignores empty strings.
   */
  renameTab = (id: string, label: string): void => {
    const trimmed = label.trim();
    if (!trimmed) {
      return;
    }

    const tab = this.tabs.find(t => t.id === id);
    if (tab) {
      tab.label = trimmed;
    }
  };

  /**
   * Switch the active tab. No-ops for unknown ids.
   *
   * When version history is open, the right panel should always track the
   * active sheet scope. If users switch tabs while that panel is visible,
   * retarget the version-history sheet and clear any preview selection that
   * belonged to the previous sheet.
   */
  setActiveTab = (id: string): void => {
    if (!this.tabs.some(t => t.id === id)) {
      return;
    }

    this.activeTabId = id;
    this._recordMountedSheetAccess(id);
    this.closeAllColumnConfigSidebars();

    if (this.isVersionHistoryOpen && this.versionHistorySheetId !== id) {
      this.versionHistorySheetId = id;
      this.resetVersionPreviewState();
    }
  };

  /**
   * Ensure a sheet tab exists (e.g. Eval SDK experiment sheet) and activate it
   * immediately. Also marks pending activation so a subsequent bootstrap
   * hydrate cannot snap back to the previous empty sheet.
   */
  activateExternalSheet = (sheet: {
    id: string;
    title?: string | null;
    index?: number | null;
  }): void => {
    const sheetId = sheet.id;
    if (!sheetId) {
      return;
    }

    if (!this.tabs.some(t => t.id === sheetId)) {
      this.tabs.push({
        id: sheetId,
        sheetId,
        label: sheet.title?.trim() || "Experiment",
      });
    }

    this.pendingActivateSheetId = sheetId;
    this.setActiveTab(sheetId);
  };

  markEvalRunning = (sheetId: string): void => {
    if (!sheetId) {
      return;
    }
    this.setEvalRunStatus(sheetId, "running");
  };

  clearEvalRunning = (sheetId: string): void => {
    this.evalRunningSheetIds = this.evalRunningSheetIds.filter(id => id !== sheetId);
    this.clearEvalExpectedRowCount(sheetId);
    if (this.evalRunStatuses[sheetId] === "running") {
      const next = { ...this.evalRunStatuses };
      delete next[sheetId];
      this.evalRunStatuses = next;
      try {
        sessionStorage.removeItem(evalRunStatusStorageKey(sheetId));
      } catch {
        // Ignore storage failures.
      }
    }
    try {
      sessionStorage.removeItem(evalRunningStorageKey(sheetId));
    } catch {
      // Ignore storage failures.
    }
  };

  /** Remember an abandoned populate so Redis expected_row_count can't re-stick Running. */
  markEvalAborted = (sheetId: string): void => {
    if (!sheetId) {
      return;
    }
    this.setEvalRunStatus(sheetId, "aborted");
  };

  setEvalRunStatus = (sheetId: string, status: EvalRunStatus): void => {
    if (!sheetId || !VALID_EVAL_RUN_STATUSES.has(status)) {
      return;
    }
    this.evalRunStatuses = {
      ...this.evalRunStatuses,
      [sheetId]: status,
    };
    try {
      sessionStorage.setItem(evalRunStatusStorageKey(sheetId), status);
    } catch {
      // Ignore storage failures.
    }

    if (status === "running") {
      this.evalAbortedSheetIds = this.evalAbortedSheetIds.filter(id => id !== sheetId);
      try {
        sessionStorage.removeItem(evalAbortedStorageKey(sheetId));
      } catch {
        // Ignore storage failures.
      }
      if (!this.evalRunningSheetIds.includes(sheetId)) {
        this.evalRunningSheetIds.push(sheetId);
      }
      try {
        sessionStorage.setItem(evalRunningStorageKey(sheetId), "1");
      } catch {
        // Ignore storage failures.
      }
      return;
    }

    // completed | aborted — leave the active Running mark, but keep planned
    // row counts so the banner can still show "N of M" after Stop / Ctrl+C.
    this.evalRunningSheetIds = this.evalRunningSheetIds.filter(id => id !== sheetId);
    try {
      sessionStorage.removeItem(evalRunningStorageKey(sheetId));
    } catch {
      // Ignore storage failures.
    }
    if (status === "aborted") {
      if (!this.evalAbortedSheetIds.includes(sheetId)) {
        this.evalAbortedSheetIds.push(sheetId);
      }
      try {
        sessionStorage.setItem(evalAbortedStorageKey(sheetId), "1");
      } catch {
        // Ignore storage failures.
      }
    }
  };

  getEvalRunStatus = (sheetId: string): EvalRunStatus | null => {
    const fromMemory = this.evalRunStatuses[sheetId];
    if (fromMemory) {
      return fromMemory;
    }
    return readEvalRunStatusFromSession(sheetId);
  };

  isEvalAborted = (sheetId: string): boolean =>
    this.getEvalRunStatus(sheetId) === "aborted" ||
    this.evalAbortedSheetIds.includes(sheetId) ||
    readEvalAbortedFromSession(sheetId);

  isEvalRunning = (sheetId: string): boolean => this.getEvalRunStatus(sheetId) === "running";

  setEvalExpectedRowCount = (sheetId: string, expectedRowCount: number): void => {
    if (!sheetId || !Number.isFinite(expectedRowCount) || expectedRowCount < 0) {
      return;
    }
    if (expectedRowCount === 0) {
      this.clearEvalExpectedRowCount(sheetId);
      return;
    }
    const normalized = Math.trunc(expectedRowCount);
    const current = this.getEvalExpectedRowCount(sheetId);
    // After abort, keep the original planned denominator ("3 of 10"). Older SDKs
    // used to snap expected_row_count down to written rows — ignore those shrinks.
    if (this.isEvalAborted(sheetId) && current !== null && normalized < current) {
      return;
    }
    this.evalExpectedRowCounts = {
      ...this.evalExpectedRowCounts,
      [sheetId]: normalized,
    };
    try {
      sessionStorage.setItem(evalExpectedRowCountStorageKey(sheetId), String(normalized));
    } catch {
      // Ignore storage failures — in-memory count still works.
    }
  };

  getEvalExpectedRowCount = (sheetId: string): number | null => {
    const fromMemory = this.evalExpectedRowCounts[sheetId];
    if (typeof fromMemory === "number" && fromMemory > 0) {
      return fromMemory;
    }
    return readExpectedRowCountFromSession(sheetId);
  };

  clearEvalExpectedRowCount = (sheetId: string): void => {
    if (!(sheetId in this.evalExpectedRowCounts)) {
      try {
        sessionStorage.removeItem(evalExpectedRowCountStorageKey(sheetId));
      } catch {
        // Ignore storage failures.
      }
      return;
    }
    const next = { ...this.evalExpectedRowCounts };
    delete next[sheetId];
    this.evalExpectedRowCounts = next;
    try {
      sessionStorage.removeItem(evalExpectedRowCountStorageKey(sheetId));
    } catch {
      // Ignore storage failures.
    }
  };

  /** Restore eval-running marks after a remount (refresh mid-run). */
  restoreEvalRunningFromSession = (sheetIds: string[]): void => {
    for (const sheetId of sheetIds) {
      if (!sheetId) {
        continue;
      }
      const status = readEvalRunStatusFromSession(sheetId);
      if (status) {
        this.evalRunStatuses = {
          ...this.evalRunStatuses,
          [sheetId]: status,
        };
        if (status === "aborted" && !this.evalAbortedSheetIds.includes(sheetId)) {
          this.evalAbortedSheetIds.push(sheetId);
        }
        if (status === "running" && !this.evalRunningSheetIds.includes(sheetId)) {
          this.evalRunningSheetIds.push(sheetId);
        }
      } else if (readEvalAbortedFromSession(sheetId)) {
        // Legacy abort mark without eval_run_status — still treat as aborted so the
        // Stopped banner stays visible after remount / hard refresh.
        this.evalRunStatuses = {
          ...this.evalRunStatuses,
          [sheetId]: "aborted",
        };
        if (!this.evalAbortedSheetIds.includes(sheetId)) {
          this.evalAbortedSheetIds.push(sheetId);
        }
      }
      if (!(this.evalRunningSheetIds.includes(sheetId) || this.isEvalAborted(sheetId))) {
        try {
          if (sessionStorage.getItem(evalRunningStorageKey(sheetId)) === "1") {
            this.evalRunningSheetIds.push(sheetId);
          }
        } catch {
          // Ignore storage failures.
        }
      }
      // Keep expected counts after abort so "N of M" still renders on the Stopped banner.
      const expected = readExpectedRowCountFromSession(sheetId);
      if (expected !== null) {
        this.evalExpectedRowCounts = {
          ...this.evalExpectedRowCounts,
          [sheetId]: expected,
        };
      }
    }
  };

  /**
   * Move a tab from `fromIndex` to `toIndex`.
   */
  moveTab = (fromIndex: number, toIndex: number): void => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= this.tabs.length ||
      toIndex >= this.tabs.length
    ) {
      return;
    }
    const [removed] = this.tabs.splice(fromIndex, 1);
    this.tabs.splice(toIndex, 0, removed);
  };

  // ── Version History ───────────────────────

  openVersionHistory = (sheetId: string): void => {
    this.versionHistoryClosing = false;
    this.versionHistorySheetId = sheetId;
    this.rightPanel = "version-history";
    this.closeAllColumnConfigSidebars();
    this.resetVersionPreviewState();
  };

  beginVersionHistoryClose = (): void => {
    this.versionHistoryClosing = true;
    this.versionHistorySheetId = null;

    if (this.rightPanel === "version-history" || this.rightPanel === "version-column-config") {
      this.rightPanel = null;
    }
  };

  closeVersionHistory = (): void => {
    // Closing the panel should also clear any active preview intent so the
    // right rail can fully dismiss instead of sticking in preview mode.
    this.resetVersionPreviewState();
    this.beginVersionHistoryClose();
  };

  completeVersionHistoryClose = (): void => {
    this.versionHistoryClosing = false;
    this.resetVersionPreviewState();
  };

  resetVersionPreviewState = (): void => {
    this.previewVersionId = null;
    this.previewLoadingVersionId = null;
    this.clearVersionColumnConfigTarget();
  };

  setPreviewVersionId = (versionId: string | null): void => {
    this.previewVersionId = versionId;
  };

  startVersionPreviewLoad = (versionId: string): void => {
    if (this.previewVersionId && this.previewVersionId !== versionId) {
      this.previewVersionId = null;
    }

    if (this.versionColumnConfigTarget && this.versionColumnConfigTarget.versionId !== versionId) {
      this.clearVersionColumnConfigTarget();
    }

    this.previewLoadingVersionId = versionId;
  };

  finishVersionPreviewLoad = (versionId?: string | null): void => {
    if (!versionId || this.previewLoadingVersionId === versionId) {
      this.previewLoadingVersionId = null;
    }
  };

  openVersionColumnConfig = (target: VersionColumnConfigTarget): void => {
    this.versionColumnConfigTarget = target;
    this.rightPanel = "version-column-config";
    this.closeAllColumnConfigSidebars();
  };

  backToVersionHistoryFromColumnConfig = (): void => {
    this.versionColumnConfigTarget = null;

    if (this.isVersionHistoryOpen || this.shouldHoldVersionPreviewState) {
      this.rightPanel = "version-history";
      return;
    }

    this.rightPanel = null;
  };

  clearVersionColumnConfigTarget = (): void => {
    this.versionColumnConfigTarget = null;

    if (this.rightPanel === "version-column-config") {
      this.rightPanel = this.isVersionHistoryOpen ? "version-history" : null;
    }
  };

  setRightPanel = (panel: SmartTableRightPanel): void => {
    this.rightPanel = panel;

    if (panel === "score" || panel === "version-history" || panel === "version-column-config") {
      this.closeAllColumnConfigSidebars();
    }

    const isVersionSurfacePanel = panel === "version-history" || panel === "version-column-config";

    if (!isVersionSurfacePanel && this.isVersionHistoryOpen) {
      this.beginVersionHistoryClose();

      // When another panel takes over immediately (e.g. switching from
      // Version History to Score), there is no rail close transition event
      // to complete the "hold preview" lifecycle. Finish it eagerly so we
      // don't keep stale preview/history state mounted.
      if (panel !== null) {
        this.completeVersionHistoryClose();
      }
    }
  };

  /**
   * Close and clear every sheet's column-config sidebar state.
   *
   * This is used whenever a global right panel (score / version history)
   * takes over the right rail so we never end up with competing panels.
   */
  closeAllColumnConfigSidebars = (): void => {
    for (const columnConfigStore of this._columnConfigs.values()) {
      columnConfigStore.sidebar.reset();
      columnConfigStore.selection.clear();
    }
  };
}
